/**
 * BankSettings — admin page for the holding to enter Indian & Qatari bank
 * details that get embedded into every generated invoice's PDF.
 */
import React, { useEffect, useState } from 'react';
import { Save, Building2, Landmark, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const FIELDS_INDIA = [
  { f: 'india_account_name', l: 'Account holder name' },
  { f: 'india_bank_name', l: 'Bank name (e.g. HDFC Bank)' },
  { f: 'india_branch', l: 'Branch' },
  { f: 'india_ifsc_code', l: 'IFSC code', mono: true },
  { f: 'india_account_number', l: 'Account number', mono: true },
  { f: 'india_upi_id', l: 'UPI ID (e.g. yourname@hdfcbank)', mono: true },
];
const FIELDS_QATAR = [
  { f: 'qatar_account_name', l: 'Account holder name' },
  { f: 'qatar_bank_name', l: 'Bank name (e.g. Qatar National Bank)' },
  { f: 'qatar_iban', l: 'IBAN', mono: true },
  { f: 'qatar_swift', l: 'SWIFT / BIC', mono: true },
];
const FIELDS_META = [
  { f: 'tax_registration', l: 'Tax registration / GST / VAT number' },
  { f: 'billing_email', l: 'Billing email', type: 'email' },
  { f: 'invoice_footer_note', l: 'Invoice footer note (visible on every invoice)', textarea: true },
];

function Section({ title, icon: Icon, children, color = '#5E1F8B' }) {
  return (
    <div className="ve-card p-6">
      <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
        <span className="h-9 w-9 rounded-md flex items-center justify-center" style={{ background: `${color}1F`, color }}>
          <Icon size={16}/>
        </span>
        <h3 className="display text-lg font-bold">{title}</h3>
      </div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field({ field, value, onChange }) {
  return (
    <label className={`block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold ${field.textarea ? 'md:col-span-2' : ''}`}>
      {field.l}
      {field.textarea ? (
        <textarea
          rows={3}
          value={value || ''}
          onChange={(e) => onChange(field.f, e.target.value)}
          data-testid={`bank-${field.f}`}
          className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal font-normal"
        />
      ) : (
        <input
          type={field.type || 'text'}
          value={value || ''}
          onChange={(e) => onChange(field.f, e.target.value)}
          data-testid={`bank-${field.f}`}
          className={`mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal font-normal ${field.mono ? 'font-mono' : ''}`}
        />
      )}
    </label>
  );
}

export default function BankSettings() {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/billing/bank-settings');
        setForm(data || {});
      } catch { /* empty */ }
      finally { setLoading(false); }
    })();
  }, []);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put('/billing/bank-settings', form);
      toast.success('Bank details saved. New invoices will use these.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save bank settings');
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="text-sm text-slate-500">Loading…</div>;
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow">Billing Settings</div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold">Receive funds.</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Add the bank or UPI accounts you want your clients to settle invoices into.
            These details are embedded in every PDF invoice you generate.
          </p>
        </div>
        <button onClick={save} disabled={busy} data-testid="bank-save" className="ve-btn-primary self-start">
          <Save size={14}/> {busy ? 'Saving…' : 'Save bank details'}
        </button>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6">
        <Section title="Indian Bank (Preferred)" icon={Landmark} color="#5E1F8B">
          {FIELDS_INDIA.map((f) => <Field key={f.f} field={f} value={form[f.f]} onChange={onChange}/>)}
        </Section>
        <Section title="Qatar Bank (Alternative)" icon={Building2} color="#0F766E">
          {FIELDS_QATAR.map((f) => <Field key={f.f} field={f} value={form[f.f]} onChange={onChange}/>)}
        </Section>
        <Section title="Invoice Meta" icon={FileText} color="#475569">
          {FIELDS_META.map((f) => <Field key={f.f} field={f} value={form[f.f]} onChange={onChange}/>)}
        </Section>
      </div>

      <div className="mt-8 ve-card p-6 border-[#8B3FC5] bg-gradient-to-br from-[#FFF8E5] to-white">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#5E1F8B] font-bold">Tabby BNPL · GCC</div>
        <h3 className="display text-lg font-bold mt-2">Coming soon — split payments via Tabby.</h3>
        <p className="text-sm text-slate-600 mt-2">
          We&apos;ve pre-wired Tabby on the backend. Add your <code className="mono text-xs bg-white px-1.5 py-0.5 rounded border">TABBY_PUBLIC_KEY</code> &amp;{' '}
          <code className="mono text-xs bg-white px-1.5 py-0.5 rounded border">TABBY_SECRET_KEY</code> to <code className="mono text-xs bg-white px-1.5 py-0.5 rounded border">/app/backend/.env</code> to enable.
        </p>
      </div>
    </div>
  );
}
